import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/forum/Navbar';
import Sidebar from '../components/common/Sidebar';
import ForumTour from '../components/tour/ForumTour';
import {
  HiPencilSquare,
  HiTrash,
  HiHandThumbUp,
  HiHandThumbDown,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi2';

const POSTS_PER_PAGE = 15;
const CACHE_DURATION = 2 * 60 * 1000; // 5 minutes
const SEARCH_DEBOUNCE_DELAY = 300;

// Enhanced cache with TTL and invalidation
class PostCache {
  constructor() {
    this.cache = new Map();
    this.lastFetch = new Map();
  }

  generatePageCacheKey(page, sort) {
    const skip = (page - 1) * POSTS_PER_PAGE;
    
    let endpoint;
    if (sort === 'latest') {
      endpoint = '/post/latest';
    } else if (sort === 'most-upvoted') {
      endpoint = '/post/most-liked';
    } else {
      endpoint = '/post';
    }

    // Create a unique key that includes page number and sort type
    return `${endpoint}_page_${page}_sort_${sort}_skip_${skip}_limit_${POSTS_PER_PAGE}`;
  }

  get(key) {
    const entry = this.cache.get(key);
    const lastFetch = this.lastFetch.get(key);

    if (entry && lastFetch && Date.now() - lastFetch < CACHE_DURATION) {
      console.log('Cache hit for:', key);
      return entry;
    }

    // Clean up expired entries
    this.cache.delete(key);
    this.lastFetch.delete(key);
    console.log('Cache miss for:', key);
    return null;
  }

  set(key, data) {
    console.log('Setting cache for:', key);
    this.cache.set(key, data);
    this.lastFetch.set(key, Date.now());
  }

  invalidate(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.lastFetch.delete(key);
      }
    }
  }

  delete(key) {
    this.cache.delete(key);
    this.lastFetch.delete(key);
  }

  clear() {
    this.cache.clear();
    this.lastFetch.clear();
  }
}

const Forum = () => {
  const {
    user,
    loading,
    canModifyPost,
    isModerator,
    deletePost,
    getAuthenticatedApi
  } = useAuth();

  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Refs for optimization
  const cache = useRef(new PostCache());
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(false);

  // Memoized values
  const totalPages = useMemo(() => Math.ceil(totalPosts / POSTS_PER_PAGE), [totalPosts]);
  const hasNextPage = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);
  const hasPrevPage = useMemo(() => currentPage > 1, [currentPage]);

  // Axios-based API call using the centralized authenticated instance
  const fetchWithAuth = useCallback(async (endpoint, config = {}) => {
    try {
      const api = await getAuthenticatedApi();
      const response = await api.get(endpoint, config);
      return response.data;
    } catch (error) {
      console.error(`API call to ${endpoint} failed:`, error);
      throw error;
    }
  }, [getAuthenticatedApi]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle success navigation message
  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.success);
      setShowSuccess(true);
      window.history.replaceState({}, document.title);
      setTimeout(() => setShowSuccess(false), 4000);
    }
  }, [location]);

  // Enhanced fetch posts with caching
  const fetchPosts = useCallback(async (page = 1, sort = 'latest', useCache = true) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const cacheKey = cache.current.generatePageCacheKey(page, sort);

    // Use cache if enabled
    if (useCache) {
      const cachedData = cache.current.get(cacheKey);
      if (cachedData) {
        if (isMounted.current) {
          setPosts(cachedData.posts);
          setTotalPosts(cachedData.total);
          setIsLoading(false);
        }
        return cachedData;
      }
    }

    setIsLoading(true);
    try {
      const skip = (page - 1) * POSTS_PER_PAGE;
      const queryString = `skip=${skip}&limit=${POSTS_PER_PAGE}`;

      const endpoint =
        sort === 'latest'
          ? '/post/latest'
          : sort === 'most-upvoted'
            ? '/post/most-liked'
            : '/post';

      const fullUrl = `${endpoint}?${queryString}`;

      console.log('Fetching:', fullUrl, 'for page:', page); // Debug log

      const data = await fetchWithAuth(fullUrl, {
        signal: abortControllerRef.current.signal,
      });
      
      console.log('Received data for page', page, ':', data); // Debug log

      // Since your API returns an array of posts, we need to estimate total
      // This is a limitation - ideally your API should return total count
      let processedData;
      if (Array.isArray(data)) {
        // If we get fewer posts than requested, we're on the last page
        const isLastPage = data.length < POSTS_PER_PAGE;
        
        if (isLastPage) {
          // If this is the last page, calculate total based on current page
          const totalEstimate = (page - 1) * POSTS_PER_PAGE + data.length;
          processedData = {
            posts: data,
            total: totalEstimate,
          };
        } else {
          // If we got full page, there might be more pages
          // We can't know exact total, so we estimate conservatively
          const totalEstimate = page * POSTS_PER_PAGE + 1; // +1 to indicate more pages exist
          processedData = {
            posts: data,
            total: totalEstimate,
          };
        }
      } else if (data.posts && data.total !== undefined) {
        processedData = { posts: data.posts, total: data.total };
      } else {
        processedData = { posts: data, total: data.length };
      }

      // Only cache if we have valid data
      if (processedData.posts.length > 0) {
        cache.current.set(cacheKey, processedData);
      }

      if (isMounted.current) {
        setPosts(processedData.posts);
        setTotalPosts(processedData.total);
      }

      return processedData;
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching posts:', error);
      if (isMounted.current) {
        setPosts([]);
        setTotalPosts(0);
      }
      return { posts: [], total: 0 };
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [fetchWithAuth]);

  // Enhanced fetch all posts with caching
  const fetchAllPostsForSearch = useCallback(async () => {
    const cacheKey = 'search_all_posts_cache';
    const cachedData = cache.current.get(cacheKey);

    if (cachedData) {
      return cachedData;
    }

    try {
      console.log('Fetching all posts for search');
      const data = await fetchWithAuth('/post');
      console.log('All posts data:', data);

      const posts = Array.isArray(data) ? data : (data.posts || []);

      cache.current.set(cacheKey, posts);
      return posts;
    } catch (error) {
      console.error('Error fetching all posts for search:', error);
      return [];
    }
  }, [fetchWithAuth]);

  // Optimized search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (searchQuery.trim()) {
        setIsSearching(true);
        setIsLoading(true);
        try {
          const allPosts = await fetchAllPostsForSearch();
          const query = searchQuery.toLowerCase();

          const filtered = allPosts.filter((post) => {
            return (
              post.title?.toLowerCase().includes(query) ||
              post.author?.name?.toLowerCase().includes(query) ||
              post.content?.toLowerCase().includes(query) ||
              (post.tags || []).some((tag) =>
                typeof tag === 'string'
                  ? tag.toLowerCase().includes(query)
                  : tag?.name?.toLowerCase().includes(query)
              )
            );
          });

          const sortedFiltered = [...filtered].sort((a, b) => {
            if (sortBy === 'latest') {
              return new Date(b.created_at) - new Date(a.created_at);
            }
            if (sortBy === 'most-upvoted') {
              const aScore = (a.like_count || 0) - (a.dislike_count || 0);
              const bScore = (b.like_count || 0) - (b.dislike_count || 0);
              return bScore - aScore;
            }
            return 0;
          });

          if (isMounted.current) {
            setPosts(sortedFiltered);
            setTotalPosts(sortedFiltered.length);
            setCurrentPage(1);
          }
        } catch (error) {
          console.error('Error during search:', error);
          if (isMounted.current) {
            setPosts([]);
            setTotalPosts(0);
          }
        } finally {
          if (isMounted.current) setIsLoading(false);
        }
      } else {
        if (isMounted.current) {
          setIsSearching(false);
          setCurrentPage(1);
        }
        // Only fetch if not searching
        if (!searchQuery.trim()) {
          fetchPosts(1, sortBy);
        }
      }
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, sortBy, fetchAllPostsForSearch, fetchPosts]);

  // Fetch posts on page/sort change (only when not searching)
  useEffect(() => {
    if (!searchQuery.trim() && !isSearching) {
      console.log('Fetching posts for page:', currentPage, 'sort:', sortBy);
      fetchPosts(currentPage, sortBy);
    }
  }, [currentPage, sortBy, fetchPosts, searchQuery, isSearching]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Enhanced delete post with cache invalidation
  const handleDeletePost = useCallback(async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const result = await deletePost(postId);
      if (result.success) {
        // Clear all cache to ensure fresh data
        cache.current.clear();

        if (isMounted.current) {
          const newTotalPosts = totalPosts - 1;
          const newTotalPages = Math.ceil(newTotalPosts / POSTS_PER_PAGE);
          const pageToLoad = currentPage > newTotalPages ? Math.max(1, newTotalPages) : currentPage;

          setTotalPosts(newTotalPosts);
          setCurrentPage(pageToLoad);

          if (!searchQuery.trim()) {
            fetchPosts(pageToLoad, sortBy, false); // Skip cache on delete
          } else {
            setPosts((prev) => prev.filter((post) => post.id !== postId));
          }

          setSuccessMessage('Post deleted successfully!');
          setShowSuccess(true);
          setTimeout(() => {
            if (isMounted.current) {
              setShowSuccess(false);
            }
          }, 4000);
        }
      } else {
        alert(result.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  }, [deletePost, totalPosts, currentPage, searchQuery, sortBy, fetchPosts]);

  const handleEditPost = useCallback((postId) => {
    navigate(`/post/edit/${postId}`);
  }, [navigate]);

  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    // Clear cache when sort changes to ensure fresh data
    cache.current.clear();
  }, []);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      console.log('Changing to page:', newPage);
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalPages]);

  // Memoized pagination component
  const PaginationControls = useMemo(() => {
    if (isSearching || totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + maxVisiblePages - 1);

        if (start > 1) {
          pages.push(1);
          if (start > 2) pages.push('...');
        }

        for (let i = start; i <= end; i++) pages.push(i);

        if (end < totalPages) {
          if (end < totalPages - 1) pages.push('...');
          pages.push(totalPages);
        }
      }
      return pages;
    };

    return (
      <div className="flex justify-center items-center space-x-2 mt-8">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          className={`p-2 rounded-lg border transition-all ${hasPrevPage
            ? 'border-gray-600 text-white hover:bg-gray-700'
            : 'border-gray-800 text-gray-600 cursor-not-allowed'
            }`}
        >
          <HiChevronLeft className="w-5 h-5" />
        </button>

        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === 'number' && handlePageChange(page)}
            disabled={page === '...'}
            className={`px-3 py-2 rounded-lg border transition-all ${page === currentPage
              ? 'border-blue-500 bg-blue-500 text-white'
              : page === '...'
                ? 'border-transparent text-gray-500 cursor-default'
                : 'border-gray-600 text-white hover:bg-gray-700'
              }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className={`p-2 rounded-lg border transition-all ${hasNextPage
            ? 'border-gray-600 text-white hover:bg-gray-700'
            : 'border-gray-800 text-gray-600 cursor-not-allowed'
            }`}
        >
          <HiChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }, [isSearching, totalPages, currentPage, hasPrevPage, hasNextPage, handlePageChange]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white">
      <Sidebar />
      <Navbar />

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed top-6 right-6 bg-green-500/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg flex items-center z-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:items-center mb-6">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-dark-gray/50 border border-gray-700 rounded-full px-4 py-2 w-full sm:max-w-sm"
          />

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="bg-dark-gray/50 border border-gray-700 text-white rounded-full px-4 py-2"
            >
              <option value="latest">Latest</option>
              <option value="most-upvoted">Most Upvoted</option>
            </select>

            {user && (
              <Link to="/create-post">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-primary hover:bg-blue-600 text-white rounded-full px-6 py-2 w-full sm:w-auto"
                >
                  New Post
                </motion.button>
              </Link>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            {searchQuery.trim() ? 'No posts found matching your search.' : 'No posts found.'}
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {posts.map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-dark-gray/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 sm:p-6 hover:bg-dark-gray/40 transition-all duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {post.author?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {post.author?.name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-gray-400">
                          {new Date(post.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>

                    {user && (canModifyPost(post) || isModerator()) && (
                      <div className="flex items-center space-x-2">
                        {canModifyPost(post) && (
                          <button
                            onClick={() => handleEditPost(post.id)}
                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                          >
                            <HiPencilSquare className="w-4 h-4" />
                          </button>
                        )}
                        {(canModifyPost(post) || isModerator()) && (
                          <button
                            onClick={() => handleDeletePost(post.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <Link to={`/post/${post.id}`} className="block">
                    <h3 className="text-xl font-semibold text-white mb-3 hover:text-blue-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-300 mb-4 line-clamp-3">{post.content}</p>

                    {post.image_url && (
                      <div className="mb-4 rounded-lg overflow-hidden">
                        <img
                          src={post.image_url.startsWith('http') ? post.image_url : `https://app.writeapp.in${post.image_url}`}
                          alt="Post attachment"
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                  </Link>

                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-4 border-t border-gray-700/50 gap-2">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center text-sm text-gray-400">
                        <HiHandThumbUp className="w-4 h-4 mr-1" />
                        {post.like_count || 0}
                      </span>
                      <span className="flex items-center text-sm text-gray-400">
                        <HiHandThumbDown className="w-4 h-4 mr-1" />
                        {post.dislike_count || 0}
                      </span>
                    </div>

                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30"
                          >
                            {typeof tag === 'string' ? tag : tag.name}
                          </span>
                        ))}
                        {post.tags.length > 3 && (
                          <span className="px-2 py-1 bg-gray-500/20 text-gray-300 text-xs rounded-full border border-gray-500/30">
                            +{post.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {PaginationControls}
          </>
        )}

        {user && (
          <>
            <ForumTour user={user} />
            <button
              onClick={() => {
                localStorage.setItem('tour_step', 'sidebar');
                window.dispatchEvent(new Event('start-tour'));
              }}
              className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full shadow-lg transition-all"
            >
              Start Tour
            </button>
          </>
        )}
      </div>
      {user && <ForumTour user={user} />}
    </div>
  );
};

export default Forum;
